import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Typography,
    Box,
    Grid,
    Paper,
    TextField,
    Avatar,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Popover,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    InputAdornment,
    IconButton,
    Button,
    Divider
} from '@mui/material';
import { DateRange, Assignment, Search, Close, KeyboardArrowDown, DoneAll, DeleteSweep } from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindPopover,
} from 'material-ui-popup-state/hooks';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useDownloadPdfCardStatementMutation, useFetchDataNoCacheQuery } from '../../../services/services';
import { encrypt } from '../../../../../utils/encrypt';

// Mock card service
interface CardStatementResponse {
    success: boolean;
    message: string;
    data?: string; // Base64 string directly
}


interface CardStatementActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CardStatementAction: React.FC<CardStatementActionProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [selectedCards, setSelectedCards] = useState<any[]>([]); // Kart objelerini tutuyoruz
    const [cardSearchText, setCardSearchText] = useState('');

    const toggleCard = (card: any) => {
        const isSelected = selectedCards.some(c => c.cardNo === card.cardNo);
        if (isSelected) {
            setSelectedCards(prev => prev.filter(c => c.cardNo !== card.cardNo));
        } else {
            setSelectedCards(prev => [...prev, card]);
        }
    };

    const selectAllCards = () => {
        // Mevcut filtrelenmiş kartlardan seçili olmayanları ekle
        const newSelected = [...selectedCards];
        filteredCards.forEach(card => {
            if (!newSelected.some(c => c.cardNo === card.cardNo)) {
                newSelected.push(card);
            }
        });
        setSelectedCards(newSelected);
    };

    const clearAllCards = () => {
        setSelectedCards([]);
    };

    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState<string>('08');
    const [currentPage, setCurrentPage] = useState(1);
    const [allCards, setAllCards] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef<boolean>(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { currentUserState } = useSelector((state: RootState) => state?.currentUserState);
    const listUrl = `/Card/CustomerCardsV2?customerId=${currentUserState?.companyId}`;
    const [requestBody, setRequestBody] = useState({
        filters: [] as any[],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });

    // customerId değiştiğinde requestBody'yi güncelle
    useEffect(() => {
        if (currentUserState?.companyId) {
            setRequestBody(prev => ({ ...prev, customerId: currentUserState.companyId }));
        }
    }, [currentUserState?.companyId]);

    const { data: fetchData, isLoading: isFetchLoading, isFetching, refetch } = useFetchDataNoCacheQuery({ url: listUrl, filter: requestBody, method: "POST" }, {
        skip: !requestBody?.customerId || !isOpen,
        refetchOnMountOrArgChange: true
    });
    const isRequestLoading = isFetching || isFetchLoading;
    const [downloadPdfCardStatement] = useDownloadPdfCardStatementMutation();

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'card-select-popup',
    });

    const isCardSelected = (cardNo: string) => {
        return selectedCards.some(c => c.cardNo === cardNo);
    };

    // Modal açıldığında state'leri resetle ve listeyi çek
    useEffect(() => {
        if (isOpen && currentUserState?.companyId) {
            // Timeout'u temizle
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
            setCurrentPage(1);
            setAllCards([]);
            setHasMore(true);
            setSelectedCards([]);
            setCardSearchText('');
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody(prev => ({ ...prev, page: 1, customerId: currentUserState?.companyId }));
            // Modal açıldığında listeyi çek - Manuel refetch'i kaldırıyoruz çünkü setRequestBody zaten tetikleyecek
        }
    }, [isOpen, currentUserState?.companyId]);

    // requestBody değiştiğinde allCards'ı resetle (yeni sayfa için)
    useEffect(() => {
        if (requestBody?.page === 1 && isOpen) {
            setAllCards([]);
        }
    }, [requestBody?.page, isOpen]);

    // Yeni data geldiğinde allCards'a ekle
    useEffect(() => {
        if (fetchData && isOpen) {
            // API response yapısı: { totalCount, pageSize, currentPage, totalPages, data: [...], success, message, statusCode }
            // RTK Query bu response'u direkt döndürüyor: fetchData = { data: [...], totalCount, ... }

            // Eğer success false veya data null ise daha fazla istek atma
            if (!fetchData.success || fetchData.data === null || fetchData.data === undefined) {
                setHasMore(false);
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                if (currentPage === 1) {
                    setAllCards([]);
                }
                return;
            }

            let newCards: any[] = [];

            // Direkt fetchData.data array olmalı
            if (Array.isArray(fetchData.data)) {
                newCards = fetchData.data;
            } else if (fetchData.data?.data && Array.isArray(fetchData.data.data)) {
                // Eğer RTK Query wrap etmişse
                newCards = fetchData.data.data;
            } else if (Array.isArray(fetchData)) {
                // fetchData direkt array ise
                newCards = fetchData;
            }

            if (Array.isArray(newCards) && newCards.length > 0) {
                if (currentPage === 1) {
                    // İlk sayfa ise direkt set et
                    setAllCards(newCards);
                } else {
                    // Sonraki sayfalar ise mevcut listeye ekle (duplicate kontrolü ile)
                    setAllCards(prev => {
                        const existingIds = new Set(prev.map((card: any) => card.id || card.cardNo));
                        const uniqueNewCards = newCards.filter((card: any) => !existingIds.has(card.id || card.cardNo));
                        return [...prev, ...uniqueNewCards];
                    });
                }

                // Eğer gelen data 25'ten az ise daha fazla veri yok - daha fazla istek atma
                if (newCards.length < 25) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            } else {
                // Data boş array veya null ise daha fazla istek atma
                if (currentPage === 1) {
                    setAllCards([]);
                }
                setHasMore(false);
            }

            // Loading bitince ref'i de false yap
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [fetchData, currentPage, isOpen, isFetchLoading]);

    // 2024'ten güncel yıla kadar olan yılları oluştur
    const generateYears = () => {
        const startYear = 2024;
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = startYear; year <= currentYear; year++) {
            years.push(year);
        }
        return years;
    };

    // Tüm ayları tanımla
    const allMonths = [
        { value: '01', label: 'Ocak' },
        { value: '02', label: 'Şubat' },
        { value: '03', label: 'Mart' },
        { value: '04', label: 'Nisan' },
        { value: '05', label: 'Mayıs' },
        { value: '06', label: 'Haziran' },
        { value: '07', label: 'Temmuz' },
        { value: '08', label: 'Ağustos' },
        { value: '09', label: 'Eylül' },
        { value: '10', label: 'Ekim' },
        { value: '11', label: 'Kasım' },
        { value: '12', label: 'Aralık' }
    ];

    // Seçilen yıla göre geçerli ayları oluştur - useMemo ile optimize
    const availableMonths = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1; // 0-11 -> 1-12
        const selectedYearNum = parseInt(selectedYear);

        // Eğer geçmiş yıl seçildiyse tüm ayları göster
        if (selectedYearNum < currentYear) {
            return allMonths;
        }

        // Eğer güncel yıl seçildiyse sadece geçmiş ve şu anki aya kadar göster
        if (selectedYearNum === currentYear) {
            return allMonths.filter(month => parseInt(month.value) <= currentMonth);
        }

        // Gelecek yıl seçildiyse hiçbir ay gösterme
        return [];
    }, [selectedYear]);

    // Yıl değiştiğinde seçili ayın geçerli olup olmadığını kontrol et
    useEffect(() => {
        const isMonthAvailable = availableMonths.some(month => month.value === selectedMonth);
        if (!isMonthAvailable && availableMonths.length > 0) {
            // Eğer seçili ay geçerli değilse, mevcut aylardan ilkini seç
            setSelectedMonth(availableMonths[availableMonths.length - 1].value);
        }
    }, [selectedYear, availableMonths, selectedMonth]);

    // Ay-yıl formatını güzelleştiren helper fonksiyon
    const formatDisplayMonth = (year: string, month: string) => {
        if (!year || !month) return '';
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('tr-TR', {
            month: 'long',
            year: 'numeric'
        });
    };

    // Kartları filtreleme (allCards üzerinden)
    const filteredCards = allCards.filter((card: any) => !!(card && card.cardNo));

    // Kart label formatı
    const formatCardLabel = (option: any) => {
        if (!option || !option.cardNo) return '';
        const name = option.embossName || option.cardName || option.customerName || '';
        return name ? `${option.cardNo} - ${name}` : option.cardNo;
    };

    // Kart arama handler'ı
    const handleCardSearch = (event: any, newInputValue: string) => {
        setCardSearchText(newInputValue);
    };

    // Sonraki sayfayı yükle
    const loadNextPage = () => {
        // Tüm kontrolleri yap: hasMore, loading durumları ve ref kontrolü
        if (!hasMore || isLoadingMore || isRequestLoading || isLoadingMoreRef.current) {
            return;
        }

        // Ref ile aynı anda birden fazla istek atılmasını engelle
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        setRequestBody(prev => ({ ...prev, page: nextPage }));
        // Manuel refetch'i kaldırıyoruz çünkü setRequestBody zaten tetikleyecek
    };

    // Arama metni değiştiğinde backend'e filtre göndererek server-side arama yap
    useEffect(() => {
        if (!isOpen) return;

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            const extraFilters = cardSearchText.trim()
                ? [{
                    field: "cardNo",
                    logicOperator: "and",
                    values: [cardSearchText.trim()],
                    valueType: 1,
                    operationType: 1
                }]
                : [];

            setAllCards([]);
            setCurrentPage(1);
            setHasMore(true);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody(prev => ({
                ...prev,
                page: 1,
                filters: extraFilters
            }));
        }, 500);

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [cardSearchText]);

    const handleRequestSubmit = () => {
        if (!selectedCards.length) {
            enqueueSnackbar('Lütfen en az bir kart seçiniz', {
                variant: 'warning'
            });
            return;
        }

        if (!selectedYear || !selectedMonth) {
            enqueueSnackbar('Lütfen yıl ve ay seçimi yapınız', {
                variant: 'warning'
            });
            return;
        }

        setShowSubmitConfirm(true);
    };

    const performSubmit = async () => {
        if (!selectedCards.length || !selectedYear || !selectedMonth) {
            return;
        }

        try {
            setLoading(true);

            // Tüm seçilen kartları tek bir request'te gönder
            const statementInfo = {
                cardNo: selectedCards.map(card => encrypt(card.cardNo)), // String array olarak tüm kartlar
                year: selectedYear,
                month: selectedMonth,
            };

            const result = await downloadPdfCardStatement(statementInfo);
            const response: CardStatementResponse | undefined = result.data;

            if (response?.success) {
                // Base64 verisini alıyoruz
                const base64Data = response.data;

                if (base64Data) {
                    // Eğer backend array döndürüyorsa (çoklu kart için)
                    if (Array.isArray(base64Data)) {

                        // Her bir base64 string'i için ayrı dosya oluştur
                        base64Data.forEach((singleBase64, index) => {
                            try {

                                if (typeof singleBase64 === 'string' && singleBase64.trim()) {
                                    const byteCharacters = atob(singleBase64.trim());
                                    const byteArrays = [];

                                    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
                                        const slice = byteCharacters.slice(offset, offset + 1024);
                                        const byteNumbers = new Array(slice.length);
                                        for (let i = 0; i < slice.length; i++) {
                                            byteNumbers[i] = slice.charCodeAt(i);
                                        }
                                        byteArrays.push(new Uint8Array(byteNumbers));
                                    }

                                    const blob = new Blob(byteArrays, { type: 'application/pdf' });
                                    const link = document.createElement('a');
                                    const url = URL.createObjectURL(blob);
                                    link.href = url;

                                    // Her kart için ayrı dosya adı
                                    const cardInfo = selectedCards[index];
                                    const cardName = cardInfo?.embossName || cardInfo?.cardName || cardInfo?.cardNo || `Kart${index + 1}`;
                                    link.download = `KartEkstresi_${cardName}_${selectedYear}-${selectedMonth}.pdf`;

                                    link.click();
                                    URL.revokeObjectURL(url);

                                }
                            } catch (fileError) {
                                enqueueSnackbar('Kart ekstresi oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.', {
                                    variant: 'error'
                                });
                            }
                        });

                        enqueueSnackbar(`${base64Data.length} kart ekstresi başarıyla indirildi`, {
                            variant: 'success'
                        });
                    } else if (typeof base64Data === 'string') {

                        try {
                            const byteCharacters = atob(base64Data.trim());
                            const byteArrays = [];

                            for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
                                const slice = byteCharacters.slice(offset, offset + 1024);
                                const byteNumbers = new Array(slice.length);
                                for (let i = 0; i < slice.length; i++) {
                                    byteNumbers[i] = slice.charCodeAt(i);
                                }
                                byteArrays.push(new Uint8Array(byteNumbers));
                            }

                            const blob = new Blob(byteArrays, { type: 'application/pdf' });
                            const link = document.createElement('a');
                            const url = URL.createObjectURL(blob);
                            link.href = url;

                            const cardCount = selectedCards.length;
                            link.download = `KartEkstreleri_${cardCount}Kart_${selectedYear}-${selectedMonth}.pdf`;

                            link.click();
                            URL.revokeObjectURL(url);

                            enqueueSnackbar('Kart ekstresi başarıyla indirildi', {
                                variant: 'success'
                            });
                        } catch (decodeError) {
                            enqueueSnackbar('PDF dosyası oluşturulurken hata oluştu. Lütfen tekrar deneyiniz.', {
                                variant: 'error'
                            });
                            return;
                        }
                    } else {
                        enqueueSnackbar('Beklenmeyen veri formatı alındı.', {
                            variant: 'error'
                        });
                        return;
                    }
                }

                // Başarılı mesajı göster
                onSuccess?.();
                handleClose();
            } else {
                // Sadece backend'ten gelen mesajı göster
                if (response?.message) {
                    enqueueSnackbar(response.message, {
                        variant: 'error'
                    });
                }
            }
        } catch (error: any) {
            // Sadece beklenmeyen kritik hatalar için mesaj göster
            // Backend'ten dönen hatalar zaten response?.message ile gösteriliyor
            console.error('Kart ekstresi oluşturma hatası:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Timeout'u temizle
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setSelectedCards([]);
        setCardSearchText('');
        setSelectedYear(new Date().getFullYear().toString());
        setSelectedMonth('08');
        setCurrentPage(1);
        setAllCards([]);
        setHasMore(true);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        setRequestBody(prev => ({ ...prev, page: 1 }));
        setShowSubmitConfirm(false);
        onClose();
    };

    const statementSubmitConfirmContent =
        selectedYear && selectedMonth
            ? `Seçilen ${selectedCards.length} kart için ${formatDisplayMonth(selectedYear, selectedMonth)} dönemi ekstresini oluşturmak istediğinize emin misiniz?`
            : '';

    return (
        <>
        <ConfirmationModal
            isOpen={isOpen}
            onClose={handleClose}
            onConfirm={handleRequestSubmit}
            title="Kart Ekstresi Oluştur"
            isLoading={false}
            confirmButtonText={selectedCards.length > 1 ? `${selectedCards.length} Kart Ekstresi Oluştur` : 'Oluştur'}
            closeButtonText="Vazgeç"
            optionalWidth="500px"
            customHeaderStyle={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                titleColor: 'white',
                icon: (
                    <Avatar sx={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        width: 32,
                        height: 32
                    }}>
                        <Assignment fontSize="small" />
                    </Avatar>
                )
            }}
        >
            <Grid container spacing={3}>
                {/* Kart Seçimi */}
                <Grid item xs={12} >
                    <Typography variant="body1" sx={{ mb: 3, color: '#64748b' }}>
                        Ekstre oluşturmak istediğiniz kartları seçiniz:
                    </Typography>

                    <Box
                        {...bindTrigger(popupState)}
                        sx={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            minHeight: '56px',
                            maxHeight: '120px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: 'primary.main'
                            },
                            position: 'relative',
                            pr: 5,
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#ccc',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#aaa',
                            }
                        }}
                    >
                        {selectedCards.length === 0 ? (
                            <Typography sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                                Kart arayın veya seçin
                            </Typography>
                        ) : (
                            selectedCards.map((card) => (
                                <Chip
                                    key={card.cardNo}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {card.cardNo}
                                            </Typography>
                                            {(card.embossName || card.cardName || card.customerName) && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                                    - {card.embossName || card.cardName || card.customerName}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                    onDelete={(e) => {
                                        e.stopPropagation();
                                        toggleCard(card);
                                    }}
                                    sx={{
                                        '& .MuiChip-deleteIcon': {
                                            fontSize: '18px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                color: '#ffffff !important',
                                                backgroundColor: '#d32f2f !important',
                                                borderRadius: '50%',
                                                transform: 'scale(1.1)'
                                            }
                                        }
                                    }}
                                />
                            ))
                        )}
                        <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.5 }}>
                            {isRequestLoading && currentPage === 1 && <CircularProgress size={20} color="inherit" />}
                            <KeyboardArrowDown sx={{ color: 'text.secondary' }} />
                        </Box>
                    </Box>

                    <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            sx: { width: 500, mt: 1, maxHeight: 450, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                        }}
                    >
                        <>
                            <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Kart No ile ara..."
                                    value={cardSearchText}
                                    onChange={(e) => setCardSearchText(e.target.value)}
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: cardSearchText && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setCardSearchText('')}>
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ mb: 1.5 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DoneAll />}
                                        onClick={selectAllCards}
                                        fullWidth
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                    >
                                        Tümünü Seç
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteSweep />}
                                        onClick={clearAllCards}
                                        fullWidth
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                    >
                                        Tümünü Kaldır
                                    </Button>
                                </Box>
                            </Box>
                            <List
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    maxHeight: 300,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#ccc',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#aaa',
                                    }
                                }}
                                onScroll={(e) => {
                                    const target = e.currentTarget;
                                    // İstek devam ediyorsa veya daha fazla veri yoksa yeni istek atma
                                    if (isRequestLoading || isLoadingMore || !hasMore || isLoadingMoreRef.current) {
                                        return;
                                    }

                                    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
                                        loadNextPage();
                                    }
                                }}
                            >
                                {isRequestLoading && currentPage === 1 ? (
                                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                        <CircularProgress size={40} thickness={4} sx={{ color: '#3b82f6' }} />
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                            Kartlar Yükleniyor...
                                        </Typography>
                                    </Box>
                                ) : filteredCards.length === 0 ? (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Kart bulunamadı
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredCards.map((card) => (
                                        <ListItem
                                            key={card.cardNo}
                                            button
                                            onClick={() => toggleCard(card)}
                                            sx={{ py: 0.5 }}
                                        >
                                            <Checkbox
                                                edge="start"
                                                checked={isCardSelected(card.cardNo)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                            <ListItemText
                                                primary={card.cardNo}
                                                secondary={card.cardName || card.embossName || card.customerName}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem' } }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', pl: 2, fontWeight: 600, fontSize: '0.85rem' }}>
                                                {card.customerName || card.embossName || card.cardName}
                                            </Typography>
                                        </ListItem>
                                    ))
                                )}
                                {(isLoadingMore || isLoadingMoreRef.current || (isRequestLoading && currentPage > 1)) && hasMore && (
                                    <ListItem sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 2,
                                        background: 'linear-gradient(to bottom, transparent, rgba(59, 130, 246, 0.05))',
                                        borderTop: '1px solid #f1f5f9',
                                        width: '100%'
                                    }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                            <CircularProgress
                                                size={24}
                                                thickness={4}
                                                sx={{ color: '#e2e8f0' }}
                                                variant="determinate"
                                                value={100}
                                            />
                                            <CircularProgress
                                                size={24}
                                                thickness={4}
                                                sx={{
                                                    color: '#3b82f6',
                                                    position: 'absolute',
                                                    left: 0,
                                                    animationDuration: '800ms'
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#3b82f6',
                                                fontWeight: 700,
                                                letterSpacing: '0.5px',
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem'
                                            }}
                                        >
                                            Kartlar Yükleniyor
                                        </Typography>
                                    </ListItem>
                                )}
                            </List>
                        </>
                    </Popover>

                    <Box sx={{ mb: 3, mt: 3 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}
                        >
                            Dönem Seçimi
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid #e0e7ff',
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    {/* Yıl Seçimi */}
                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                        <InputLabel>Yıl Seçiniz</InputLabel>
                                        <Select
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            label="Yıl Seçiniz"
                                            sx={{
                                                borderRadius: 2,
                                                background: 'white',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#3b82f6',
                                                    },
                                                },
                                                '&.Mui-focused': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#1d4ed8',
                                                    },
                                                }
                                            }}
                                        >
                                            {generateYears().map(year => (
                                                <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Ay Seçimi */}
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Ay Seçiniz</InputLabel>
                                        <Select
                                            value={availableMonths.some(m => m.value === selectedMonth) ? selectedMonth : (availableMonths[availableMonths.length - 1]?.value ?? '')}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            label="Ay Seçiniz"
                                            sx={{
                                                borderRadius: 2,
                                                background: 'white',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#3b82f6',
                                                    },
                                                },
                                                '&.Mui-focused': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#1d4ed8',
                                                    },
                                                }
                                            }}
                                        >
                                            {availableMonths.map(month => (
                                                <MenuItem key={month.value} value={month.value}>
                                                    {month.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {selectedYear && selectedMonth && (
                                        <Typography variant="body2" sx={{ color: '#3b82f6', mt: 2, textAlign: 'center', fontWeight: 600 }}>
                                            Seçilen Dönem: {formatDisplayMonth(selectedYear, selectedMonth)}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>

                </Grid>
            </Grid>
        </ConfirmationModal>
        <ConfirmationModal
            isOpen={showSubmitConfirm}
            onClose={() => setShowSubmitConfirm(false)}
            onConfirm={() => void performSubmit()}
            title="İşlem Onayı"
            content={statementSubmitConfirmContent}
            confirmButtonText={loading ? 'Oluşturuluyor...' : 'Onayla'}
            closeButtonText="İptal"
            isLoading={loading}
            optionalWidth="440px"
        />
        </>
    );
};

export default CardStatementAction;
